import { Datasets } from "../../../../constants/enums"
import { WARNING_MSG } from "../../../../constants/tooltips"
import { SUPPORT_REFER } from "../../../../errors/errorMessages"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { snackbar } from "../../../../utils/general/generalUtils"
import { postClientProtected, postClientPublic } from "../../clients/clients"
import { largeAttachmentMapper } from "../../mappers/attachments/attachmentMapper"
import {
    GET_ATTACHMENT_DOWNLOAD_URL,
    GET_ATTACHMENT_DOWNLOAD_URL_PUBLIC,
} from "../../urls/private"

interface IAttachmentDownload {
    downloadAttachment: (
        deviceSysRef: string,
        fileName: string,
        dataset: Datasets,
        attachmentUuid?: string,
        isAmendment?: boolean
    ) => Promise<void>
}

/**
 * Hook to facilitate downloading a device attachment. Exposes `downloadAttachment`
 * function which is used to call a specified GET attachment API.
 * @returns {IAttachmentDownload} - loading and error states.
 */

const useAttachmentDownload = (): IAttachmentDownload => {
    const { isSignedIn } = useGetUserData()

    const downloadAttachment = async (
        deviceSysRef: string,
        fileName: string,
        dataset: Datasets,
        attachmentUuid?: string,
        isAmendment?: boolean
    ): Promise<void> => {
        const client = isSignedIn ? postClientProtected : postClientPublic
        try {
            const res = await client(
                isSignedIn
                    ? GET_ATTACHMENT_DOWNLOAD_URL(dataset)
                    : GET_ATTACHMENT_DOWNLOAD_URL_PUBLIC(dataset),
                {
                    deviceRef: deviceSysRef,
                    fileName,
                    deviceType: dataset,
                    attachment_uuid: attachmentUuid,
                    isAmendment,
                }
            )
            const attachmentApiRes = largeAttachmentMapper.map(res)
            snackbar(WARNING_MSG, "default")
            const downloadLink = document.createElement("a")
            downloadLink.href = attachmentApiRes.download_url
            downloadLink.download = attachmentApiRes.fileName
            downloadLink.click()
        } catch (error) {
            const appError = parseErrorAsObj(error)
            const msg =
                appError.code === 400
                    ? "This file has just been uploaded and we are still processing it. Please try again in a few minutes!"
                    : `Unable to download. ${SUPPORT_REFER}`

            snackbar(msg, "warning")
        }
    }

    return {
        downloadAttachment,
    }
}

export default useAttachmentDownload
